/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect, useLayoutEffect } from 'react'
import { Alert } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch } from 'react-redux'
import { ToastsStore } from 'react-toasts'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'

import { TOAST_EXPIRATION } from 'constant'

import useAuthProvider from 'hooks/useAuthProvider'
import useClasses from 'hooks/useClasses'
import useChat from 'hooks/useChat'
import useHttpClient from 'hooks/useHttpClient'
import useInterval from 'hooks/useInterval'
import useQueryValidation from 'hooks/useQueryValidation'
import useQuery from 'hooks/useQuery'
import useSocket from 'hooks/useSocket'
import useSounds from 'hooks/useSounds'
import useToggler from 'hooks/useToggler'

import Animate from 'components/Animate'
import Countdown from 'react-countdown'
import Chat from './components/chat/Chat'
import ClassRoom from './components/classRoom/ClassRoom'
import FlexContainer from 'components/FlexContainer'
import Template from 'components/Template'
import Text from 'components/Text'

import PATH from 'utils/path'
import { ChatContext, ControlsContext } from 'store/context'
import { EXPIRE_CLASS_ROOM } from 'providers/events/socket'

import roles from 'utils/roles'
import { WHITE } from 'assets/colors'
import { fetchClassRoomThunk } from 'store/@thunks/classes'
import { endClassRoomConnection } from 'store/@reducers/classes'
import { fetchOutgoingClassThunk } from 'store/@thunks/schedules'
import { img } from 'assets/img'

/**
 * @type {number}
 * @description
 * This will make a emit for the socket to check the status of meeting.
 * Equivalent to 2 minutes per request.
 */
const classStatusInterval = 1000 * 60 * 2

const Meetings = ({ history }) => {
  const { t } = useTranslation()

  const user = useAuthProvider()

  const { token: room } = useQuery()

  const chat = useChat({ room })

  const dispatch = useDispatch()

  const socket = useSocket()

  const sounds = useSounds()

  const meeting = useHttpClient({
    endpoint: '/api/v1/meetings',
    queries: {
      room
    },
    method: 'GET',
    requiresAuth: true
  })

  const classes = useClasses()

  const [active, setIsActive] = useToggler(true)

  const [debounce] = useDebouncedCallback(chat.onTypingMessage, 1000)

  useLayoutEffect(() => {
    document.body.style.backgroundColor = '#2C3E50'

    return () => {
      document.body.style.backgroundColor = WHITE
    }
  }, [])

  const fetchClassRoom = () => {
    dispatch(fetchClassRoomThunk({ token: room }))
      .then(unwrapResult)
      .then(() => dispatch(fetchOutgoingClassThunk()))
      .catch(({ message }) => {
        ToastsStore.warning(message)

        history.push(PATH.DASHBOARD)
      })
  }

  const socketRef = useCallback(() => {
    if (active) {
      /**
       * @description
       * Check that the current schedule has been expired.
       */
      socket.on(EXPIRE_CLASS_ROOM, payload => {
        console.info('ping', payload)

        if (payload.room === room) {
          setIsActive(false)
        }
      })
    }
  }, [active, room, setIsActive, socket])

  /**
   * @description
   * If the class is not longer active, we support at least 5 minutes extras.
   * To close the class connection automatically.
   */
  const classConnection = () => {
    if (!active) {
      ToastsStore.warning(t('TOAST_NOTIFICATION.minutesLeft'), TOAST_EXPIRATION)

      sounds.play('ping')
    }
  }
  /**
   * @description
   * Fetch the classRoom.
   */
  useEffect(fetchClassRoom, [meeting.data])

  /**
   * @description
   * This hook will allow to check if the class has the given extra minutes.
   */
  useEffect(classConnection, [active])

  /**
   * @description
   * When meeting status is checked, run this handler.
   */
  useEffect(socketRef, [active])

  /**
   * @description
   * If the token is not present, we should throw an exception and redirect.
   */
  useQueryValidation(
    {
      required: ['token']
    },
    exception => {
      if (exception) {
        history.push(PATH.DASHBOARD)
      }
    }
  )

  /**
   * @description
   * Every n minutes will check the status of the meeting.
   */
  useInterval(() => {
    if (socket.disconnected) {
      ToastsStore.warning(
        t('TOAST_NOTIFICATION.serverProblem'),
        TOAST_EXPIRATION
      )

      history.push(PATH.DASHBOARD)
    }
  }, classStatusInterval)

  /**
   * @requires useDebounce to increase perfomance.
   * @param {import ('components/Chat').Typing} message
   */
  const onTypingMessage = message => {
    debounce(message)
  }

  const handleEndClass = () => {
    sounds.play('ping', { volume: 0.5 })

    ToastsStore.success(t('TOAST_NOTIFICATION.classEnded'), TOAST_EXPIRATION)

    dispatch(endClassRoomConnection())

    /**
     * @description
     * Doesn't require the feedback if user is a teacher.
     */
    if (user.profile.role === roles.TEACHER) {
      return history.push(PATH.DASHBOARD)
    }

    history.push({
      pathname: PATH.QUALITY,
      state: {
        feedback: true
      },
      search: `?token=${room}`
    })
  }

  const date = Date.now() + 1000 * 60 * 4

  return (
    <Template
      withLoader={classes.loading || meeting.loading}
      loaderIndicatorName={t('TOAST_NOTIFICATION.preparingStreaming')}
    >
      {meeting.data.token && (
        <Animate>
          {active || (
            <Alert variant="warning">
              <Alert.Heading>
                <Text bold center tag="h5">
                  {t('TOAST_NOTIFICATION.automaticallyEnd')}{' '}
                  <img alt="timer" src={img.time} />
                </Text>
              </Alert.Heading>
              <FlexContainer>
                <Countdown date={date} onComplete={handleEndClass} />
              </FlexContainer>
            </Alert>
          )}
          <ChatContext.Provider value={chat.unreads}>
            <ControlsContext.Provider value={chat.onToggleChat}>
              <ClassRoom name={room} token={meeting.data.token} />
            </ControlsContext.Provider>
          </ChatContext.Provider>
          {chat.visible && (
            <Chat
              action={chat.typing.text}
              messages={chat.messages}
              onSendMessage={chat.onChatMessage}
              onSwitch={chat.onToggleChat}
              onTyping={onTypingMessage}
              unreads={chat.unreads}
            />
          )}
        </Animate>
      )}
    </Template>
  )
}

export default memo(Meetings)
